// ─── Shared ──────────────────────────────────────────────────────────────────

export interface KsecMeta {
  requestId: string
  timestamp: string
}

export interface KsecResponse<T> {
  data: T
  meta: KsecMeta
}

export interface KsecError {
  error: {
    code: string
    message: string
    status: number
  }
  meta: KsecMeta
}

export interface KsecPagination {
  page: number
  size: number
  total: number
  totalPages: number
}

export interface KsecPaginatedData<T> {
  items: T[]
  pagination: KsecPagination
}

// ─── Action ENUM — fuente de verdad de acciones válidas ──────────────────────

/**
 * Enum de acciones válidas en la plataforma Kashio.
 * Fuente de verdad para TODOS los componentes y servicios del frontend.
 *
 * Semántica:
 *   ALL → lectura + escritura + administración
 *   RW  → lectura + escritura
 *   R   → solo lectura
 */
export enum ActionEnum {
  ALL = 'ALL',
  RW  = 'RW',
  R   = 'R',
}

/** Tipo derivado del ActionEnum. Usar en todas las props, DTOs y validaciones. */
export type ActionType = `${ActionEnum}`

/** Array de valores válidos para validación y selectores de UI. */
export const VALID_ACTIONS: readonly ActionType[] = [ActionEnum.ALL, ActionEnum.RW, ActionEnum.R]

// ─── Access actions ───────────────────────────────────────────────────────────

/** @deprecated Usar ActionType en su lugar. */
export type AccessAction = ActionType

// ─── Identity / Me Context (KSEC v3.0) ───────────────────────────────────────

/**
 * Shape of identity returned by GET /me/context (v3.0).
 * Sin email, sin partyPublicId — la identidad se resuelve por DI.public_id.
 */
export interface KsecMeIdentity {
  identityPublicId: string
  identityType: 'individual' | 'party_role'
  individualIdentified: string | null
  nickname: string | null
  statusCode: number
  status: string
}

/**
 * Tenant/organización visible para el usuario (v3.0).
 * Reemplaza a KsecVisibleOrg del esquema anterior.
 */
export interface KsecMeTenant {
  businessTenantPublicId: string
  permissionName: string
  involvementRole: string | null
}

/** Producto con acceso efectivo del usuario, tal como lo devuelve /me/context (v3.0) */
export interface KsecMeProduct {
  assetId: string
  name: string
  /** SMALLINT (1=WEB_APPLICATION, 2=API, 3=SERVICE, 4=MICROFRONTEND…). Ya no es string. */
  type: number
  /** Roles que el usuario tiene asignados en este producto */
  userRoles: string[]
  /**
   * URL del portal externo para SSO, tomada del campo portal_url de la oferta en KBRM.
   * El frontend concatena el id_token para construir el deep-link.
   * Null si KBRM no devuelve oferta o el producto no tiene portal externo configurado.
   */
  portal_url: string | null
  offering_name: string | null
  offering_id: string | null
}

export interface KsecMeContext {
  identity: KsecMeIdentity
  tenants: KsecMeTenant[]
  effectiveAccessSummary: {
    totalAccess: number
    distinctAssets: number
    distinctItems: number
  }
  products: KsecMeProduct[]
}

// ─── Effective Access (KSEC v3.0) ────────────────────────────────────────────
// Forma real del response de GET /auth/effective-access/:identityId

export interface KsecAccessResource {
  /** Nombre descriptivo del DigitalAssetItem */
  name: string
  /** tag_code del item — null si no tiene código asignado en el catálogo */
  tagCode: string | null
  /** SMALLINT: 1=MENU, 2=PAGE, 3=COMPONENT, 4=ENDPOINT, 5=OPTION */
  itemType: number
}

export interface KsecAccessItem {
  permissionId: string
  origin: 'ENTITLEMENT' | 'PRIVILEGE'
  action: string
  function: string | null
  resource: KsecAccessResource
  asset: { name: string; type: number }
  involvementRole: string | null
  businessTenantPublicId: string
  granterPublicId: string | null
  granterType: string | null
  expiresAt: string | null
}

export interface KsecEffectiveAccess {
  /** DI.public_id del usuario evaluado (v3.0 — era partyPublicUuid en versiones anteriores) */
  identityPublicId: string
  /** Siempre false en v3.0 — el campo is_kashio_super fue eliminado del schema */
  isKashioSuper: boolean
  totalAccess: number
  computedAt: string
  access: KsecAccessItem[]
}

// ─── Validate Access ──────────────────────────────────────────────────────────

export interface KsecValidateAccessRequest {
  /** DI.public_id del usuario evaluado (v3.0 — era partyPublicUuid en v2). */
  identityPublicId: string
  tagCode: string
  action: AccessAction
}

export interface KsecValidateAccessResult {
  allowed: boolean
  action: AccessAction | null
  tagCode: string
  origin: string
  isKashioSuper: boolean
}

// ─── List response (pagination lives in meta, data is a direct array) ─────────

export interface KsecListPagination {
  page: number
  size: number
  totalElements: number
  totalPages: number
}

export interface KsecListMeta {
  timestamp: string
  requestId: string
  pagination: KsecListPagination
}

export interface KsecListResponse<T> {
  data: T[]
  meta: KsecListMeta
}

// ─── Digital Identity (KSEC v3.0) ────────────────────────────────────────────

/**
 * DigitalIdentityDto devuelto por GET /digital-identities/by-auth0-sub/{sub}
 * y GET /digital-identities/public-id/{id}.
 * v3.0: sin email, sin auth0UserId, sin organizationPartyPublicId.
 */
export interface KsecDigitalIdentity {
  id: string
  /** Alias de individualIdentified — UUID del Party en KBRM. Puede ser null si la identidad es de tipo rol. */
  subjectPartyPublicId: string | null
  subjectPartyRolePublicId: string | null
  individualIdentified: string | null
  partyRoleIdentified: string[] | null
  nickname: string | null
  statusCode: number
  status: string
  validFor: { startDate: string | null; endDate: string | null }
  creationDate: string
  lastUpdate: string
}

export interface KsecCreateIdentityRequest {
  /** UUID del Individual/Party en KBRM (→ individual_identified). */
  subjectPartyPublicId?: string
  subjectPartyRolePublicId?: string
  organizationPartyPublicId?: string
  email: string
  nickname: string
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING'
  initialPassword?: string
}

export interface KsecUpdateIdentityRequest {
  nickname?: string
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'BLOCKED'
}

// ─── Identity with full access ────────────────────────────────────────────────

export interface KsecIdentityAccess {
  identity: KsecDigitalIdentity
  credentials: unknown[]
  permissions: KsecPermission[]
}

// ─── Credentials ─────────────────────────────────────────────────────────────

export interface KsecCreateCredentialRequest {
  credentialSubType: 'LOGIN_PASSWORD'
  password: string
  syncAuth0: boolean
}

export interface KsecCredential {
  id: string
  credentialSubType: string
  status: string
  createdAt: string
}

// ─── Roles ───────────────────────────────────────────────────────────────────

export interface KsecRoleCatalog {
  id: string
  involvementRole: string
  name: string
  description?: string | null
  businessTenantPublicId?: string
}

export interface KsecRolesCatalogData {
  items?: KsecRoleCatalog[]
}

export type InvolvementRole =
  | 'ADMIN'
  | 'USER'
  | 'READER'
  | 'ACCOUNT_MANAGER'
  | 'ACCOUNT_CONSULTANT'
  | 'COUNTRY_MANAGER'
  | 'SALES_MANAGER'
  | 'CUSTOMER_ADMINISTRATOR'
  | 'SYSTEM_ADMIN'
  | 'KASHIO_SUPER_ADMIN'
  | 'KASHIO_ADMIN'
  | 'OTHER'

export interface KsecCreateRoleRequest {
  name: string
  description?: string
  involvementRole: InvolvementRole
  businessTenantPublicId: string
}

export interface KsecUpdateRoleRequest {
  name?: string
  description?: string
}

export interface KsecCreateEntitlementRequest {
  digitalAssetItemPublicId: string
  action: ActionType
  function?: string
}

// ─── Permissions ─────────────────────────────────────────────────────────────

export interface KsecAssetUserRole {
  id: string
  roleId: string
  roleName: string
  involvementRole: string | null
  creationDate: string
}

/** PermissionDto v3.0 — granter/user son objetos polimórficos */
export interface KsecPermission {
  id: string
  name: string
  description: string | null
  businessTenantPublicId: string
  granter: { publicId: string | null; type: string | null }
  user: { publicId: string | null; type: string | null }
  validFor: { startDate: string | null; endDate: string | null }
  creationDate: string
  assignedRoles: KsecAssetUserRole[]
}

/** 'Party' = individual; 'PartyRole' = party role */
export type PartyRefType = 'Party' | 'PartyRole'

export interface KsecCreatePermissionRequest {
  /** Nombre del permiso — requerido, mínimo 2 caracteres */
  name: string
  /** UUID del tenant (businessTenantPublicId) — requerido en v3.0 */
  businessTenantPublicId: string
  /** UUID del otorgante (Party o PartyRole) */
  granterPublicId?: string | null
  granterType?: PartyRefType | null
  /** UUID del usuario receptor (Party o PartyRole) */
  userPublicId?: string | null
  userType?: PartyRefType | null
  roleIds: string[]
  description?: string
  validForStart?: string | null
  validForEnd?: string | null
}

// ─── Privileges ──────────────────────────────────────────────────────────────

/** @deprecated Usar ActionType en su lugar. */
export type PrivilegeAction = ActionType

export interface KsecPrivilege {
  id: string
  permissionId: string
  digitalAssetItemId: string
  action: PrivilegeAction
  function: string | null
  creationDate: string
}

export interface KsecCreatePrivilegeRequest {
  digitalAssetItemPublicId: string
  action: PrivilegeAction
  /** Descripción funcional del privilegio (requerido por el backend) */
  function: string
}

// ─── Entitlements ────────────────────────────────────────────────────────────

export interface KsecEntitlement {
  id: string
  roleId: string
  digitalAssetItemId: string
  digitalAssetItemTagCode: string | null
  action: ActionType
  function: string | null
  creationDate: string
}

// ─── Digital Assets ───────────────────────────────────────────────────────────

export interface KsecDigitalAsset {
  id: string
  name: string
  /** SMALLINT: 1=WEB_APPLICATION, 2=API, 3=SERVICE, 4=MICROFRONTEND */
  type: number
  /** SMALLINT: 1=ACTIVE, 2=DEPRECATED */
  status: number
  description?: string | null
  parentAssetPublicId?: string | null
}

export interface KsecDigitalAssetItemDetail {
  id: string
  name: string
  tagCode: string | null
  /** SMALLINT: 1=PAGE, 2=OPTION, 3=API_ENDPOINT */
  type: number
}

export interface KsecDigitalAssetRole {
  id: string
  involvementRole: string
  name?: string
}

// ─── Party Role Sync (EP3) ────────────────────────────────────────────────────

/** Producto resuelto durante la sincronización de PartyRole. */
export interface KsecSyncedProduct {
  assetId: string
  name: string
  type: number
  productSpecificationPublicId: string
  itemCount: number
}

/**
 * Respuesta del endpoint POST /party-roles/:partyRolePublicId/sync-products.
 * Operación de solo lectura: consulta KBRM y devuelve los productos del usuario.
 * No crea ni modifica registros en BD.
 */
export interface KsecSyncProductsResult {
  partyRolePublicId: string
  organizationPublicId: string
  kbrmProductCodes: string[]
  productsFound: KsecSyncedProduct[]
}

// ─── Organization User Create (POST /create-users/organizations) ──────────────

export interface KsecCreateOrgUserRequest {
  /** ID del tenant / organización en KBRM (ej: "org_xxx" o businessTenantPublicId). */
  party_id: string
  /**
   * ID del PartyRole del individuo en KBRM.
   * Opcional: si se omite, el backend lo deriva de la respuesta de KBRM al crear el individuo.
   */
  partyRolePublicId?: string | null
  /**
   * PartyRole del usuario que realiza la acción (el admin que crea al nuevo usuario).
   * Si se envía → granter_type = 'PartyRole' (admin → empleado).
   * Si se omite → granter_type = 'Party' (Kashio Platform → admin).
   */
  callerPartyRolePublicId?: string | null
  email: string
  password: string
  firstName: string
  lastName: string
  phone?: string | null
  nickname?: string | null
  /** involvementRole del rol principal: "ADMIN" | "USER" | "READER" | "SUPER_USER" */
  rol: string
  kcore?: {
    kcore_company_id?: string | null
    kcore_user_id?: string | null
    kcore_is_admin?: boolean
    timezone?: number
  } | null
}

export interface KsecCreateOrgUserEntitlementsSync {
  synced: boolean
  entitlementsCreated: number
  productsFound: number
  error?: string | null
}

export interface KsecCreateOrgUserResult {
  identity: {
    id: string
    /** public_id del Individual en KBRM (ind_xxx). Usar para PATCH /create-users/organizations/:individualPublicId. */
    individualPublicId: string
    /** public_id del PartyRole en KBRM (prl_xxx). Referencia para operaciones KBRM. */
    partyRolePublicId: string
  }
  credential: { id: string }
  assignedRole: { id: string; involvementRole: string; rol: string }
  permission: { id: string }
  assetUserRole: { id: string }
  auth0UserId: string
  entitlementsSync?: KsecCreateOrgUserEntitlementsSync
}

// ─── Organization User Update (PATCH /create-users/organizations/:individualPublicId) ──

/**
 * Todos los campos son opcionales. El servidor actualiza solo los que se envían.
 * Reglas de negocio (validadas en KSEC):
 *   - `firstName` y `lastName` deben enviarse juntos (o ninguno).
 *   - `rol` requiere también `party_id`.
 */
export interface KsecUpdateOrgUserRequest {
  /** ID del tenant/organización. Requerido solo cuando se actualiza `rol`. */
  party_id?: string | null
  email?: string | null
  password?: string | null
  firstName?: string | null
  lastName?: string | null
  /** Enviar `null` para eliminar el teléfono. Omitir para no modificarlo. */
  phone?: string | null
  nickname?: string | null
  rol?: string | null
}

export interface KsecUpdateOrgUserResult {
  /** public_id del Individual en KBRM (ind_xxx) — identifica al usuario actualizado. */
  individualPublicId: string
  updated: {
    nickname?: string
    email?: boolean
    password?: boolean
    phone?: boolean
    rol?: string
    auth0Synced: boolean
  }
}

// ─── Attachments ─────────────────────────────────────────────────────────────

export interface KsecAttachment {
  id: string
  attachmentType: string
  url?: string
  name: string
  mimeType: string
  createdAt: string
}

export interface KsecCreateAttachmentRequest {
  attachmentType: string
  name: string
  mimeType: string
  url?: string
  content?: string
}
